import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { authorizedAxiosInstance } from '@/clients';
import { DataSourceType } from '@/types/api/modules/business-intelligence';

import { GLOBAL_TRADE_DATASET_ID, TradeDataSource } from './model';

interface GlobalTradeCatalogResponse {
    categories?: Array<{
        key: string;
        label: string;
    }>;
    dataSources?: Array<{
        categoryKey: string;
        description?: string;
        key: string;
        label: string;
    }>;
}

export const useGlobalTradeCatalog = () => {
    const { data, error, isLoading } = useQuery({
        queryKey: ['datasetSchema', 5, DataSourceType.GLOBAL_TRADE, GLOBAL_TRADE_DATASET_ID],
        queryFn: async () => {
            const response = await authorizedAxiosInstance.get<GlobalTradeCatalogResponse>('/api/datasets/schema', {
                params: {
                    category: GLOBAL_TRADE_DATASET_ID,
                    type: DataSourceType.GLOBAL_TRADE,
                },
            });

            return response.data;
        },
        gcTime: 15 * 60 * 1000,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        retry: false,
        staleTime: 5 * 60 * 1000,
    });
    const sources = useMemo<TradeDataSource[]>(
        () =>
            (data?.dataSources ?? []).map((source) => ({
                category: source.categoryKey,
                description: source.description,
                id: source.key,
                label: source.label,
            })),
        [data?.dataSources],
    );

    return {
        categories: data?.categories ?? [],
        error,
        loading: isLoading,
        sources,
    };
};
